import { useState, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Receipt, Clock, FileText, FileCheck, CheckCircle2, AlertCircle } from "lucide-react";

// Status configurations
const STATUS_CONFIGS = {
    estimated: {
        label: "Estimated",
        variant: "outline",
        className: "border-slate-400 text-slate-600",
        icon: Clock,
    },
    draft: {
        label: "Draft",
        variant: "secondary",
        className: "bg-slate-200 text-slate-700",
        icon: FileText,
    },
    issued: {
        label: "Issued",
        variant: "default",
        className: "bg-blue-500 text-white",
        icon: FileCheck,
    },
    paid: {
        label: "Paid",
        variant: "default",
        className: "bg-green-500 text-white",
        icon: CheckCircle2,
    },
};

const fmt = (n, currency = "USD") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
    }).format(n);

// Get charge status
const getChargeStatus = (charge, type) => {
    if (type === "invoice") {
        if (charge.paymentStatus === "paid") return "paid";
        if (charge.invoiceStatus === "issued" || charge.invoiceStatus === "invoiced") return "issued";
        if (charge.invoiceStatus === "draft") return "draft";
        return "estimated";
    } else {
        if (charge.paymentStatus === "paid") return "paid";
        if (charge.billStatus === "issued" || charge.billStatus === "billed") return "issued";
        if (charge.billStatus === "draft") return "draft";
        return "estimated";
    }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIGS[status] || STATUS_CONFIGS.estimated;
    const Icon = config.icon;

    return (
        <Badge variant={config.variant} className={`${config.className} gap-1 text-xs`}>
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

// Calculate USD equivalent
const calculateUSDEquivalent = (charge) => {
    const quantity = parseFloat(charge.quantity) || 1;
    const amount = parseFloat(charge.amount) || 0;
    const roe = parseFloat(charge.roe) || 1;

    if (charge.currency === "USD" || !charge.currency) {
        return quantity * amount;
    }
    return (quantity * amount) / roe;
};

export function ChargeSelectionDialog({
    open,
    onOpenChange,
    type,
    partyName,
    charges,
    onProceed,
}) {
    // Filter to only show charges that are not already invoiced/billed
    const pendingCharges = useMemo(() => {
        return charges.filter((c) => {
            const status = getChargeStatus(c, type);
            // Only allow selection of estimated or draft charges
            return status === "estimated";
        });
    }, [charges, type]);

    const [selectedIds, setSelectedIds] = useState(() =>
        new Set(pendingCharges.map((c) => c.id))
    );

    const toggleId = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === pendingCharges.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(pendingCharges.map((c) => c.id)));
        }
    };

    const selectedCharges = useMemo(() =>
        pendingCharges.filter((c) => selectedIds.has(c.id)),
        [selectedIds, pendingCharges]
    );

    const selectedTotal = useMemo(() =>
        selectedCharges.reduce((sum, c) => sum + calculateUSDEquivalent(c), 0),
        [selectedCharges]
    );

    const alreadyLinkedCount = charges.length - pendingCharges.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Receipt className="h-5 w-5 text-blue-500" />
                        Select Charges for {type === "invoice" ? "Invoice" : "Vendor Bill"}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {type === "invoice" ? "Bill To" : "Vendor"}: <strong>{partyName}</strong>
                    </p>
                </DialogHeader>

                {/* Info banner if some charges are already linked */}
                {alreadyLinkedCount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 text-sm">
                        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="text-amber-700 dark:text-amber-300">
                            {alreadyLinkedCount} charge(s) already linked to {type === "invoice" ? "an invoice" : "a bill"} and excluded from selection.
                        </span>
                    </div>
                )}

                <div className="flex-1 overflow-auto">
                    <Table>
                        <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <TableRow>
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={selectedIds.size === pendingCharges.length && pendingCharges.length > 0}
                                        onCheckedChange={toggleAll}
                                        disabled={pendingCharges.length === 0}
                                    />
                                </TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-center">Currency</TableHead>
                                <TableHead className="text-center">Qty</TableHead>
                                <TableHead className="text-right">Rate</TableHead>
                                <TableHead className="text-center">ROE</TableHead>
                                <TableHead className="text-right">USD Equiv.</TableHead>
                                <TableHead className="text-center">Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {pendingCharges.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                                            <span>All charges have been {type === "invoice" ? "invoiced" : "billed"}</span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pendingCharges.map((c) => {
                                    const status = getChargeStatus(c, type);
                                    return (
                                        <TableRow
                                            key={c.id}
                                            className={`hover:bg-muted/30 cursor-pointer ${selectedIds.has(c.id) ? "bg-blue-50 dark:bg-blue-900/20" : ""}`}
                                            onClick={() => toggleId(c.id)}
                                        >
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedIds.has(c.id)}
                                                    onCheckedChange={() => toggleId(c.id)}
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium">{c.description || "-"}</TableCell>
                                            <TableCell className="text-center">{c.currency || "USD"}</TableCell>
                                            <TableCell className="text-center">{c.quantity || 1}</TableCell>
                                            <TableCell className="text-right">
                                                {parseFloat(c.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell className="text-center">{c.roe || "1.00"}</TableCell>
                                            <TableCell className="text-right font-medium text-blue-600">
                                                {fmt(calculateUSDEquivalent(c))}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <StatusBadge status={status} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                <DialogFooter className="flex items-center justify-between sm:justify-between border-t pt-4">
                    <div className="text-sm space-y-1">
                        <div className="font-medium">
                            Selected: <span className="text-blue-600">{selectedIds.size}</span> of {pendingCharges.length} charges
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                            Total: {fmt(selectedTotal)}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={selectedIds.size === 0}
                            onClick={() => onProceed(Array.from(selectedIds))}
                            className={type === "invoice" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"}
                        >
                            <Receipt className="w-4 h-4 mr-2" />
                            Create {type === "invoice" ? "Invoice" : "Bill"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
